import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAlerts } from "@/contexts/AlertsContext";
import { computeCrowding } from "@/lib/signals";
import { goldUsdCrossRead, oilRiskCrossRead } from "@/lib/crossReads";
import { fetchTopHeadlines, checkSetups, fetchRiskTone } from "@/lib/api";

const SESSION_KEY = "cot_synopsis_session";
const FLASH_SEEN = "cot_flash_seen_v1";
const JOURNAL_SEEN = "cot_journal_events_v1";
const CROSSREAD_SEEN = "cot_crossread_status_v1";

const readSet = (k) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(k) || "[]"));
  } catch {
    return new Set();
  }
};
const writeSet = (k, set) => {
  try {
    localStorage.setItem(k, JSON.stringify([...set].slice(-80)));
  } catch {
    /* ignore */
  }
};

// Build a one-glance market synopsis from data already in cache.
function buildSynopsis(markets, bias, vix, threshold) {
  const parts = [];
  if (bias?.bias && bias.bias !== "neutral") {
    parts.push(`USD ${String(bias.bias).toUpperCase()} ${bias.confidence ?? "?"}%`);
  } else if (bias?.bias) {
    parts.push("USD bias NEUTRAL");
  }
  const crowd = computeCrowding(markets);
  if (crowd?.clustered) {
    parts.push(`Crowded USD ${crowd.dominant === "long" ? "LONG" : "SHORT"} ${crowd.votes}/${crowd.total}`);
  }
  const extremes = markets
    .filter((m) => m.extreme && m.cot_index_1y != null)
    .sort((a, b) => Math.abs(b.cot_index_1y - 50) - Math.abs(a.cot_index_1y - 50))
    .slice(0, 3)
    .map((m) => `${m.symbol} ${m.extreme === "long" ? "long" : "short"}`);
  if (extremes.length) parts.push(`Extremes: ${extremes.join(", ")}`);
  if (vix != null) {
    const off = threshold != null && vix >= threshold;
    parts.push(`Risk-${off ? "OFF" : "ON"} (VIX ${vix.toFixed(1)})`);
  }
  return parts.join(" · ");
}

// Client-only alert engine — three types: market synopsis (once/session),
// high-impact flash news, and journal target/stop notifications (notify-only).
export default function AlertsEngine({ markets = [], bias, vix, threshold }) {
  const { pushAlerts } = useAlerts();
  const synopsisFired = useRef(false);

  // 1) Market synopsis — once per browser session, when core data is ready.
  useEffect(() => {
    if (synopsisFired.current) return;
    if (!markets.length || bias == null) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) {
        synopsisFired.current = true;
        return;
      }
    } catch {
      /* ignore */
    }
    const body = buildSynopsis(markets, bias, vix, threshold);
    if (!body) return;
    synopsisFired.current = true;
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
    const item = { key: `synopsis-${Date.now()}`, type: "synopsis", severity: "info", title: "Market synopsis", body };
    pushAlerts([item]);
    toast(item.title, { description: item.body });
  }, [markets, bias, vix, threshold, pushAlerts]);

  // 2) Flash news — surface genuinely new high-impact headlines (Claude-ranked).
  const newsQ = useQuery({
    queryKey: ["top-headlines"],
    queryFn: () => fetchTopHeadlines(false),
    staleTime: 1000 * 60 * 20,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    const headlines = newsQ.data?.headlines;
    if (!headlines?.length) return;
    const seen = readSet(FLASH_SEEN);
    const firstRun = seen.size === 0;
    const fresh = headlines.filter((h) => h.link && !seen.has(h.link));
    headlines.forEach((h) => h.link && seen.add(h.link));
    writeSet(FLASH_SEEN, seen);
    if (firstRun || !fresh.length) return; // seed silently on first ever load
    const items = fresh.slice(0, 2).map((h) => ({
      key: `flash-${h.link}`,
      type: "flash",
      severity: "high",
      title: "Flash · " + (h.markets?.length ? h.markets.join("/") : "Market"),
      body: h.title,
      link: h.link,
    }));
    pushAlerts(items);
    items.forEach((a) => toast.warning(a.title, { description: a.body }));
  }, [newsQ.data, pushAlerts]);

  // 3) Journal target/stop — poll live spot vs tracked open setups (notify-only).
  const checkQ = useQuery({
    queryKey: ["setups-check"],
    queryFn: checkSetups,
    refetchInterval: 1000 * 120,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  useEffect(() => {
    const events = checkQ.data?.events;
    if (!events?.length) return;
    const seen = readSet(JOURNAL_SEEN);
    const fresh = events.filter((e) => !seen.has(`${e.id}-${e.event}`));
    events.forEach((e) => seen.add(`${e.id}-${e.event}`));
    writeSet(JOURNAL_SEEN, seen);
    if (!fresh.length) return;
    const items = fresh.map((e) => {
      const hit = e.event === "target";
      return {
        key: `journal-${e.id}-${e.event}`,
        type: "journal",
        severity: hit ? "med" : "high",
        title: `${e.instrument} ${e.direction} ${hit ? "hit TARGET 🎯" : "hit STOP ✖"}`,
        body: `Live ${e.spot} reached your ${hit ? "target" : "stop"} at ${e.level}. Mark it in the Journal.`,
      };
    });
    pushAlerts(items);
    items.forEach((a) => (a.severity === "high" ? toast.error : toast.success)(a.title, { description: a.body }));
  }, [checkQ.data, pushAlerts]);

  // 4) Cross-reads — ping the moment Gold⇄USD or Oil⇄Risk flips INTO conflict.
  const riskQ = useQuery({
    queryKey: ["risk-tone"],
    queryFn: fetchRiskTone,
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (!markets.length || bias == null || !riskQ.data) return;
    const gold = markets.find((m) => m.symbol === "GOLD");
    const wti = markets.find((m) => m.symbol === "WTI");
    const gr = goldUsdCrossRead(gold, bias);
    const or = oilRiskCrossRead(wti, riskQ.data);
    let stored = {};
    try {
      stored = JSON.parse(localStorage.getItem(CROSSREAD_SEEN) || "{}");
    } catch {
      stored = {};
    }
    const items = [];
    if (gr.status === "conflict" && stored.gold !== "conflict") {
      items.push({ key: `xr-gold-${Date.now()}`, type: "crossread", severity: "high", title: "Conflict · Gold ⇄ USD", body: gr.note });
    }
    if (or.status === "conflict" && stored.oil !== "conflict") {
      items.push({ key: `xr-oil-${Date.now()}`, type: "crossread", severity: "high", title: "Conflict · Oil ⇄ Risk", body: or.note });
    }
    try {
      localStorage.setItem(CROSSREAD_SEEN, JSON.stringify({ gold: gr.status, oil: or.status }));
    } catch {
      /* ignore */
    }
    if (items.length) {
      pushAlerts(items);
      items.forEach((a) => toast.warning(a.title, { description: a.body }));
    }
  }, [markets, bias, riskQ.data, pushAlerts]);

  return null;
}
